import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import SearchSelect, { MultiSearchSelect } from '../components/SearchSelect/SearchSelect';
import p from './Pages.module.css';

export default function Translate() {
  const api = useApi();
  const toast = useToast();

  const [langName, setLangName] = useState('');
  const [langCode, setLangCode] = useState('');
  const [langIsoCode, setLangIsoCode] = useState('');

  const [transEntity, setTransEntity] = useState('');
  const [transClubId, setTransClubId] = useState('');
  const [transLangId, setTransLangId] = useState('');
  const [transContent, setTransContent] = useState('');

  const [clubs, setClubs] = useState([]);
  const [clubsFetched, setClubsFetched] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [languagesFetched, setLanguagesFetched] = useState(false);

  const [translations, setTranslations] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [selectedLangIso, setSelectedLangIso] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [editedContent, setEditedContent] = useState({});

  const fetchClubs = async () => {
    if (clubsFetched) return;
    try { const d = await api.get('/v1/club/'); setClubs(d); setClubsFetched(true); } catch {}
  };

  const fetchLanguages = async () => {
    if (languagesFetched) return;
    try { const d = await api.get('/v1/language/'); setLanguages(d); setLanguagesFetched(true); } catch {}
  };

  const loadTranslations = useCallback(async () => {
    const params = [];
    if (selectedEntityTypes.length > 0) params.push(`entity=${selectedEntityTypes.join(',')}`);
    if (selectedLangIso) params.push(`language=${selectedLangIso}`);
    let url = '/v1/translation/';
    if (params.length > 0) url += `?${params.join('&')}`;
    try { const d = await api.get(url); setTranslations(d || []); setEditedContent({}); } catch {}
  }, [api, selectedEntityTypes, selectedLangIso]);

  const loadEntityTypes = useCallback(async () => {
    try { const d = await api.get('/v1/translation/entities'); if (Array.isArray(d)) setEntityTypes(d); } catch {}
  }, [api]);

  useEffect(() => { loadTranslations(); loadEntityTypes(); }, []); // eslint-disable-line
  useEffect(() => { loadTranslations(); }, [selectedEntityTypes, selectedLangIso]); // eslint-disable-line

  const createLanguage = async () => {
    if (!langName || !langCode) { toast.error('Name and code required'); return; }
    try {
      await api.post('/v1/language/', { name: langName, code: langCode, isoCode: langIsoCode || null });
      toast.success('Language created'); setLangName(''); setLangCode(''); setLangIsoCode(''); setLanguagesFetched(false);
    } catch (e) { toast.error(e.message); }
  };

  const createTranslation = async () => {
    if (!transEntity || !transClubId || !transLangId) { toast.error('Entity, club, and language required'); return; }
    try {
      await api.post('/v1/translation/', { entity: transEntity, entityId: Number(transClubId), languageId: Number(transLangId), content: transContent || null });
      toast.success('Translation created'); setTransEntity(''); setTransClubId(''); setTransLangId(''); setTransContent(''); await loadTranslations();
    } catch (e) { toast.error(e.message); }
  };

  const updateTranslation = async (trans) => {
    const content = editedContent[trans.id];
    if (content === undefined) return;
    try {
      const r = await api.put(`/v1/translation/${trans.id}`, { entity: trans.entity, entityId: trans.entityId, languageId: trans.languageId, content: content || null });
      toast.success('Translation updated');
      setTranslations(prev => prev.map(t => t.id === trans.id ? r : t));
      setEditedContent(prev => { const c = { ...prev }; delete c[trans.id]; return c; });
    } catch (e) { toast.error(e.message); }
  };

  const filtered = useMemo(() => {
    if (!listSearch) return translations;
    const l = listSearch.toLowerCase();
    return translations.filter(t => t.entity?.toLowerCase().includes(l) || t.content?.toLowerCase().includes(l));
  }, [translations, listSearch]);

  const langMap = useMemo(() => {
    const m = {}; languages.forEach(l => { m[l.id] = l.name; }); return m;
  }, [languages]);

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle}>Translate</h1>
        <p className={p.pageSubtitle}>Manage languages and translations</p>
      </div>

      {/* Create Language */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}><h3 className={p.sectionCardTitle}>Create Language</h3></div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields}>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Name</label>
              <input className={p.fieldInput} type="text" value={langName} onChange={e => setLangName(e.target.value)} placeholder="Name" />
            </div>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Code</label>
              <input className={p.fieldInput} type="text" value={langCode} onChange={e => setLangCode(e.target.value)} placeholder="Code" />
            </div>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>ISO Code</label>
              <input className={p.fieldInput} type="text" value={langIsoCode} onChange={e => setLangIsoCode(e.target.value)} placeholder="ISO" />
            </div>
            <button className={`${p.btnPrimary} ${p.btnSmall}`} onClick={createLanguage}>Create</button>
          </div>
        </div>
      </div>

      {/* Create Translation */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}><h3 className={p.sectionCardTitle}>Create Translation</h3></div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields} style={{ marginBottom: 12 }}>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Entity</label>
              <input className={p.fieldInput} type="text" value={transEntity} onChange={e => setTransEntity(e.target.value)} placeholder="Entity" />
            </div>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Club</label>
              <SearchSelect options={clubs.map(c => ({ value: c.id, label: `${c.name} [${c.code}]` }))} value={transClubId} onChange={setTransClubId} placeholder="Select club…" width="220px" onOpen={fetchClubs} />
            </div>
            <div className={p.fieldGroup}>
              <label className={p.fieldLabel}>Language</label>
              <SearchSelect options={languages.map(l => ({ value: l.id, label: `${l.name} [${l.code}]` }))} value={transLangId} onChange={setTransLangId} placeholder="Select language…" width="220px" onOpen={fetchLanguages} />
            </div>
          </div>
          <div className={p.formGroup}>
            <label className={p.formLabel}>Content</label>
            <textarea className={p.formTextarea} value={transContent} onChange={e => setTransContent(e.target.value)} placeholder="Translation content…" rows={3} />
          </div>
          <button className={p.btnPrimary} onClick={createTranslation}>Create Translation</button>
        </div>
      </div>

      {/* Translation List */}
      <div className={p.sectionCard}>
        <div className={p.sectionCardHeader}>
          <h3 className={p.sectionCardTitle}>Translations</h3>
        </div>
        <div className={p.sectionCardBody}>
          <div className={p.inlineFields} style={{ marginBottom: 16 }}>
            <input className={p.searchInput} placeholder="Search entity/content…" value={listSearch} onChange={e => setListSearch(e.target.value)} />
            <MultiSearchSelect options={entityTypes.map(e => ({ value: e, label: e }))} selected={selectedEntityTypes} onChange={setSelectedEntityTypes} placeholder="Filter entity types…" width="260px" />
            <SearchSelect
              options={[{ value: '', label: 'All languages' }, ...languages.map(l => ({ value: l.isoCode || l.code, label: `${l.name} [${l.isoCode || l.code}]` }))]}
              value={selectedLangIso} onChange={setSelectedLangIso} placeholder="Filter language…" width="200px" onOpen={fetchLanguages}
            />
          </div>
          {filtered.length > 0 ? filtered.map(trans => (
            <div key={trans.id} className={p.card} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 120px' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent)' }}>{trans.entity}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>ID: {trans.entityId}</div>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', minWidth: 80 }}>{langMap[trans.languageId] || `Lang #${trans.languageId}`}</div>
              <div style={{ flex: 1 }}>
                <textarea
                  className={p.formInput}
                  style={{ minHeight: 32, resize: 'vertical', fontSize: '0.82rem' }}
                  rows={1}
                  value={editedContent[trans.id] !== undefined ? editedContent[trans.id] : (trans.content || '')}
                  onChange={e => setEditedContent(prev => ({ ...prev, [trans.id]: e.target.value }))}
                />
              </div>
              <button className={`${p.btnDefault} ${p.btnSmall}`} onClick={() => updateTranslation(trans)}>Update</button>
            </div>
          )) : (
            <div className={p.emptyState}><div className={p.emptyText}>No translations found</div></div>
          )}
        </div>
      </div>
    </div>
  );
}

